import { create } from 'zustand';
import { ProfileService, type Profile } from '@/entities/Profile';
import { CenterOnboardingService } from '../api/centerOnboardingService';
import type { OnboardingAwareCenter, OnboardingBusinessProfile, OnboardingState } from '../model/types';
import { normalizeBusinessProfile, normalizeOnboardingState } from '../model/helpers';

interface OnboardingStoreState {
  center: OnboardingAwareCenter | null;
  isOpen: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  dismissedRouteKey: string | null;
}

interface OnboardingStoreActions {
  load: (centerId: string | null, userId: string | null, role: string | null, routeKey: string) => Promise<void>;
  dismiss: (routeKey: string) => void;
  reopen: () => void;
  openManual: (centerId: string, userId: string | null) => Promise<void>;
  saveBusinessProfile: (
    centerId: string,
    userId: string,
    businessProfile: OnboardingBusinessProfile,
    onboardingState: OnboardingState
  ) => Promise<void>;
  saveProgress: (centerId: string, userId: string, onboardingState: OnboardingState) => Promise<void>;
  complete: (centerId: string, userId: string, onboardingState: OnboardingState) => Promise<void>;
}

type OnboardingStore = OnboardingStoreState & OnboardingStoreActions;

const service = new CenterOnboardingService();
const profileService = new ProfileService();

const buildUserScopedCenter = (
  center: OnboardingAwareCenter,
  profile: Profile | null
): OnboardingAwareCenter => ({
  ...center,
  onboarding_state: normalizeOnboardingState(
    profile?.center_onboarding_state as Partial<OnboardingState> | null | undefined
  ),
  onboarding_business_profile: normalizeBusinessProfile(
    profile?.center_onboarding_business_profile as Partial<OnboardingBusinessProfile> | null | undefined
  ),
});

const shouldOpenOnboarding = (
  center: OnboardingAwareCenter | null,
  profile: Profile | null,
  dismissedRouteKey: string | null,
  routeKey: string
) =>
  !!center && !profile?.center_onboarding_completed_at && dismissedRouteKey !== routeKey;

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  center: null,
  isOpen: false,
  isLoading: false,
  isSaving: false,
  error: null,
  dismissedRouteKey: null,

  load: async (centerId, userId, role, routeKey) => {
    if (role !== 'center_manager' || !centerId || !userId) {
      set({ center: null, isOpen: false, isLoading: false, error: null, dismissedRouteKey: null });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const [center, profile] = await Promise.all([
        service.getOnboarding(centerId),
        profileService.getByUserId(userId),
      ]);
      const mergedCenter = buildUserScopedCenter(center, profile);
      const shouldOpen = shouldOpenOnboarding(center, profile, get().dismissedRouteKey, routeKey);
      set({
        center: mergedCenter,
        isLoading: false,
        isOpen: shouldOpen,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load onboarding',
      });
    }
  },

  dismiss: (routeKey) => {
    set({ isOpen: false, dismissedRouteKey: routeKey });
  },

  reopen: () => {
    set((state) => ({
      center: state.center
        ? {
            ...state.center,
            onboarding_state: normalizeOnboardingState(state.center.onboarding_state),
          }
        : state.center,
      isOpen: true,
      dismissedRouteKey: null,
    }));
  },

  openManual: async (centerId, userId) => {
    set({ isLoading: true, error: null, dismissedRouteKey: null });

    try {
      const [center, profile] = await Promise.all([
        service.getOnboarding(centerId),
        userId ? profileService.getByUserId(userId) : Promise.resolve(null),
      ]);
      set({
        center: buildUserScopedCenter(center, profile),
        isLoading: false,
        isOpen: true,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to open onboarding',
      });
      throw error;
    }
  },

  saveBusinessProfile: async (centerId, userId, businessProfile, onboardingState) => {
    set({ isSaving: true, error: null });

    try {
      const [center, profile] = await Promise.all([
        service.updateBusinessProfile(centerId, businessProfile, onboardingState),
        profileService.updateCenterOnboardingProgress(userId, {
          center_onboarding_business_profile: businessProfile,
          center_onboarding_state: onboardingState,
          center_onboarding_completed_at: null,
        }),
      ]);
      const mergedCenter = buildUserScopedCenter(center, profile);
      set({ center: mergedCenter, isSaving: false, isOpen: !profile?.center_onboarding_completed_at, dismissedRouteKey: null });
    } catch (error) {
      set({
        isSaving: false,
        error: error instanceof Error ? error.message : 'Failed to save business profile',
      });
      throw error;
    }
  },

  saveProgress: async (centerId, userId, onboardingState) => {
    set({ isSaving: true, error: null });

    try {
      const businessProfile = normalizeBusinessProfile(
        get().center?.onboarding_business_profile as Partial<OnboardingBusinessProfile> | null | undefined
      );
      const [center, profile] = await Promise.all([
        service.updateProgress(centerId, onboardingState),
        profileService.updateCenterOnboardingProgress(userId, {
          center_onboarding_business_profile: businessProfile,
          center_onboarding_state: onboardingState,
          center_onboarding_completed_at: null,
        }),
      ]);
      const mergedCenter = buildUserScopedCenter(center, profile);
      set({ center: mergedCenter, isSaving: false, isOpen: !profile?.center_onboarding_completed_at, dismissedRouteKey: null });
    } catch (error) {
      set({
        isSaving: false,
        error: error instanceof Error ? error.message : 'Failed to save onboarding progress',
      });
      throw error;
    }
  },

  complete: async (centerId, userId, onboardingState) => {
    set({ isSaving: true, error: null });

    try {
      const completedAt = onboardingState.completedAt ?? new Date().toISOString();
      const finalState = {
        ...onboardingState,
        completedAt,
      };
      const businessProfile = normalizeBusinessProfile(
        get().center?.onboarding_business_profile as Partial<OnboardingBusinessProfile> | null | undefined
      );
      const [center, profile] = await Promise.all([
        service.complete(centerId, finalState),
        profileService.updateCenterOnboardingProgress(userId, {
          center_onboarding_business_profile: businessProfile,
          center_onboarding_state: finalState,
          center_onboarding_completed_at: completedAt,
        }),
      ]);
      set({ center: buildUserScopedCenter(center, profile), isSaving: false, isOpen: false, dismissedRouteKey: null });
    } catch (error) {
      set({
        isSaving: false,
        error: error instanceof Error ? error.message : 'Failed to complete onboarding',
      });
      throw error;
    }
  },
}));
